import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import { FlightRostersQuery } from '../../generated/graphql';

type Segment =
  FlightRostersQuery['flightRosters']['items'][number]['outboundSegments'][number];

type Props = {
  segments: Segment[];
};

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'MMM d, h:mm a');
  } catch {
    return '-';
  }
};

const formatRoute = (segment: Segment): string =>
  `${segment.departAirport ?? '???'} to ${segment.arriveAirport ?? '???'}`;

export default function StopsTooltip({ segments }: Props) {
  const stops = segments.length - 1;
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
  });

  if (stops <= 0) return null;

  return (
    <Popover
      as="span"
      className="relative ml-2 inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <PopoverButton
        as="span"
        ref={setReferenceElement}
        className="inline-block cursor-help rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
      >
        +{stops} stop{stops > 1 ? 's' : ''}
      </PopoverButton>
      {open &&
        createPortal(
          <PopoverPanel
            static
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            className="z-50 w-72 rounded-lg border border-gray-200 bg-white p-3 text-left text-xs shadow-lg"
          >
            <div className="mb-2 text-[10px] font-semibold uppercase text-gray-500">
              Itinerary
            </div>
            <ol className="space-y-2">
              {segments.map((segment, index) => (
                <li
                  key={segment.id ?? index}
                  className="flex flex-col gap-0.5 border-l-2 border-gray-200 pl-3"
                >
                  <div className="font-mono text-[11px] font-semibold text-gray-900">
                    {[segment.airline, segment.flightNumber]
                      .filter(Boolean)
                      .join(' ') || 'Flight'}
                  </div>
                  <div className="text-gray-700">{formatRoute(segment)}</div>
                  <div className="text-gray-500">
                    {formatTime(segment.departAt)}
                    <span className="mx-1 text-gray-400">to</span>
                    {formatTime(segment.arriveAt)}
                  </div>
                </li>
              ))}
            </ol>
          </PopoverPanel>,
          document.body,
        )}
    </Popover>
  );
}
